import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, tap, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { CasiUserStory, IProjectFromWeb } from '../models/models.lib';
import { ApiService } from '../api/api.service';
import { SettingsService } from '../settings/settings.service';
import { LoggerService } from '../shared/logger.service';
import { Settings, UserStorySearchOptions } from '../common/obj.lib';

export abstract class CasiUserStoryService extends BehaviorSubject<Array<any>> {
  public loading: boolean = false;
  private settings: Settings = this.settingsService.getSettings();

  constructor(
    private http: HttpClient,
    private settingsService: SettingsService
  ) {
    super([]);
  }

  public query(id: number): void {
    this.fetch(id).subscribe((x) => {
      return super.next(x);
    });
  }

  protected fetch(id: number): Observable<CasiUserStory[]> {
    this.loading = true;
    this.settings = this.settingsService.getSettings();
    return this.http.get(encodeURI(`${this.settings.apiUrl}/userstoryview/byproject/foruser/${id}`))
      .pipe(
        map((response: any, i) => {
          return <IProjectFromWeb[]>response;
        }),
        map(r => r.map(x => new CasiUserStory(x))),
        tap(() => (this.loading = false))
      )
  }
}

@Injectable({
  providedIn: 'root'
})
export class UserStoryService extends CasiUserStoryService {

  constructor(
    http: HttpClient,
    private settingService: SettingsService,
    private apiService: ApiService,
    private logger: LoggerService
  ) {
    super(http, settingService);
  }

  get() {
    return this.apiService.getHttpCall('userstory', 'get');
  }

  getById(id: number): Promise<any> {
    return this.apiService.getHttpCallAsPromise(`userstory/${id}`, 'getById');
  }

  getByProjectId(id: number): Promise<any> {
    return this.apiService.getHttpCallAsPromise(`userstory/byproject/${id}`, 'getByProjectId');
  }

  getViewByProjectId(id: number): Promise<any> {
    return this.apiService.getHttpCallAsPromise(`userstoryview/byproject/foruser/${id}`, 'getViewByProjectId');
  }

  getBySearchId(projectId: number, id: number): Promise<any> {
    return this.apiService.getHttpCallAsPromise(`userstory/bystatus/${projectId}/${id}`, 'getBySearchId');
  }

  //will use claims email if email param here is null
  searchForUser(searchStr: string | undefined, options: UserStorySearchOptions): Promise<any> {
    this.loading = true;
    return this.apiService.postHttpCallAsPromise('userstory/searchperuser', { email: options.email, searchStr: searchStr, params: options }, 'searchForUser')
      .then(data => {
        this.loading = false;
        return data.map((d: any) => new CasiUserStory(d));
      })
      .catch(err => {
        this.loading = false;
        return Promise.reject(err);
      });
  }

  searchForUserForId(searchStr: string | undefined, options: UserStorySearchOptions, id: any): Promise<any> {
    this.loading = true;
    return this.apiService.postHttpCallAsPromise('userstory/searchperuserid', { email: options.email, searchStr: searchStr, params: options, id: id }, 'searchForUserForId')
      .then(data => {
        this.loading = false;
        return data.map((d: any) => new CasiUserStory(d));
      })
      .catch(err => {
        this.loading = false;
        return Promise.reject(err);
      });
  }

  getUserStoryHistory(userStoryId: number): Promise<CasiUserStory[]> {
    this.loading = true;
    return this.apiService.getHttpCallAsPromise(`userstory/history/${userStoryId}`, 'getUserStoryHistory')
      .then(data => {
        this.loading = false;
        return data.map((d: any) => new CasiUserStory(d));
      })
      .catch(err => {
        this.loading = false;
        return Promise.reject(err);
      });
  }

  add(p: any): Promise<any> {
    return this.apiService.postHttpCallAsPromise('userstory', p, 'add');
  }

  update(p: any): Promise<CasiUserStory> {
    return this.apiService.putHttpCallAsPromise(`userstory/${p.id}`, p, 'update')
      .then(resp => {
        return new CasiUserStory(resp[0]);
        //  if(resp && resp.length > 0){
        //     }

      })
      .catch(err => Promise.reject(err));
  }

  destroy(id: number): Promise<any> {
    return this.apiService.deleteHttpCallAsPromise(`userstory/${id}`, 'destroy');
  }

  fetchHistoricFileList(id: any): Promise<any> {
    return this.apiService.getHttpCallAsPromise(`userstory/historical/${id}`, 'get');
  }


  move(itemId: number, nearestId: number): Promise<any> {
    return this.apiService.putHttpCallAsPromise('userstory/move', { itemId, nearestId }, 'move')
  }
}
